/**
 * APPLICATION CONSTANTS
 * Centralized configuration for all magic numbers and app-wide settings.
 * 
 * Why this file exists:
 * - No more hunting for hardcoded values across components
 * - Easy to update limits, defaults, and settings in one place
 * - Better maintainability and fewer bugs
 */

// =============================================================================
// STORAGE KEYS
// =============================================================================

export const STORAGE_KEYS = {
  /** Current logged-in user */
  USER: 'afriqahome_user',
  /** Saved/favorited properties */
  SAVED_PROPERTIES: 'afriqahome_saved',
  /** Agent profiles data */
  AGENTS: 'afriqahome_agents',
  /** Marketing campaigns */
  CAMPAIGNS: 'afriqahome_campaigns',
  /** Transactions history */
  TRANSACTIONS: 'afriqahome_transactions',
  /** Spotlight overrides (admin) */
  SPOTLIGHT: 'afriqahome_spotlight',
  /** Flagged properties (admin) */
  FLAGGED: 'afriqahome_flagged',
  /** Custom listings created by agents */
  CUSTOM_LISTINGS: 'afriqahome_custom_listings',
  /** Dismissed campaign (session) */
  DISMISSED_CAMPAIGN: 'afriqahome_dismissed_campaign',
  /** Chat conversations */
  CONVERSATIONS: 'afriqahome_conversations',
} as const;

// =============================================================================
// FILTER DEFAULTS
// =============================================================================

export const FILTER_DEFAULTS = {
  /** Minimum price filter (KES) */
  MIN_PRICE: 1_000_000,
  /** Maximum price filter (KES) */
  MAX_PRICE: 200_000_000,
  /** Default max price for filter slider (KES) */
  DEFAULT_MAX_PRICE: 200_000_000,
  /** Price step for slider (KES) */
  PRICE_STEP: 1_000_000,
  /** Minimum area filter (sqm) */
  MIN_AREA: 0,
  /** Maximum area filter (sqm) */
  MAX_AREA: 10_000,
} as const;

// =============================================================================
// LISTING LIMITS
// =============================================================================

export const LISTING_LIMITS = {
  /** Maximum images per listing */
  MAX_IMAGES: 10,
  /** Maximum description length (characters) */
  MAX_DESCRIPTION_LENGTH: 2000,
  /** Maximum title/address length (characters) */
  MAX_TITLE_LENGTH: 100,
  /** Minimum images required */
  MIN_IMAGES: 1,
} as const;

// =============================================================================
// LISTING PRICE RANGE
// =============================================================================

/**
 * Accepted price range (KES) for an agent-entered listing price.
 *
 * MIN is a sanity floor, not a market floor. The previous KES 1,000,000 minimum
 * blocked the entire sub-1M plot segment — a large, real market in Kenya — from
 * ever being listed. One floor applies to every property type; type-aware floors
 * were considered and rejected.
 *
 * Shared by AgentNewListing (including project units) and AgentEditListing so the
 * create and edit forms cannot drift apart. Client-side only: the listing-service
 * edge function does not validate price.
 */
export const LISTING_PRICE = {
  /** Minimum accepted listing price (KES) */
  MIN: 100_000,
  /** Maximum accepted listing price (KES) */
  MAX: 200_000_000,
} as const;

const formatKesAmount = (amount: number): string => amount.toLocaleString('en-US');

/** True when a price sits inside the accepted listing range. */
export const isListingPriceInRange = (price: number): boolean =>
  Number.isFinite(price) && price >= LISTING_PRICE.MIN && price <= LISTING_PRICE.MAX;

/**
 * The message shown when a price falls outside the range. Names both bounds so
 * an agent knows what to type next instead of just being blocked.
 */
export const LISTING_PRICE_RANGE_MESSAGE = `Price must be between KES ${formatKesAmount(
  LISTING_PRICE.MIN
)} and KES ${formatKesAmount(LISTING_PRICE.MAX)}`;

/** Same range, phrased as a hint under the price field. */
export const LISTING_PRICE_RANGE_HINT = `Enter a price between KES ${formatKesAmount(
  LISTING_PRICE.MIN
)} and KES ${formatKesAmount(LISTING_PRICE.MAX)}`;

/** Compact form of the range, for tight layouts such as the project-unit rows. */
export const LISTING_PRICE_RANGE_SHORT = `KES ${formatKesAmount(
  LISTING_PRICE.MIN
)} - ${formatKesAmount(LISTING_PRICE.MAX)}`;

// =============================================================================
// UI SETTINGS
// =============================================================================

export const UI_SETTINGS = {
  /** Items per page in listings grid */
  LISTINGS_PER_PAGE: 12,
  /** Debounce delay for search input (ms) */
  SEARCH_DEBOUNCE_MS: 300,
  /** Toast notification duration (ms) */
  TOAST_DURATION_MS: 3000,
  /** Modal animation duration (ms) */
  MODAL_ANIMATION_MS: 200,
  /** Map default zoom level */
  MAP_DEFAULT_ZOOM: 12,
} as const;

// =============================================================================
// VALIDATION PATTERNS
// =============================================================================

export const VALIDATION = {
  /** Kenyan phone number pattern (+254 or 07/01) */
  PHONE_REGEX: /^(\+254|0)[17]\d{8}$/,
  /** Email pattern */
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  /** Minimum password length */
  MIN_PASSWORD_LENGTH: 8,
} as const;

// =============================================================================
// API & EXTERNAL SERVICES
// =============================================================================

export const EXTERNAL_SERVICES = {
  /** Default avatar generator URL */
  DEFAULT_AVATAR_URL: 'https://ui-avatars.com/api/',
  /** Map tile provider (if using Leaflet) */
  MAP_TILE_URL: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
} as const;

// =============================================================================
// FEATURE FLAGS
// =============================================================================

export const FEATURE_FLAGS = {
  /** Show dev toolbar (disable in production) */
  SHOW_DEV_TOOLBAR: true,
  /** Enable AI assistant (currently removed) */
  ENABLE_AI_ASSISTANT: false,
  /** Enable map view */
  ENABLE_MAP_VIEW: true,
  /** Enable social sharing */
  ENABLE_SHARING: true,
} as const;

// =============================================================================
// SUPPORT CONTACT
// =============================================================================

export const SUPPORT = {
  /**
   * The support inbox already used across the app (Auth.tsx reactivation
   * banner, Terms/Privacy, Contact page, account deletion). Centralised here so
   * error states that need a human can link to it without a fresh hardcode.
   */
  EMAIL: 'info@afriqahome.com',
} as const;

// =============================================================================
// AGENT LANDING PAGE (/join)
// =============================================================================

export const JOIN_PAGE = {
  /**
   * WhatsApp number for the concierge "send us your listings" link on /join.
   * International format, digits only (no `+`, no spaces).
   */
  WHATSAPP_NUMBER: '254793051115',
  /** Pre-filled message for the WhatsApp concierge link. */
  WHATSAPP_MESSAGE:
    "Hi, I'd like help setting up my agent profile on Afriqahome. Here are my listings:",
  /**
   * Product screenshot shown on screen 2 (agent dashboard). Dirie supplies the
   * real asset at this path (public/images/join-product-screenshot.png). The
   * card adapts to the image's aspect ratio and stays completely hidden until
   * the image actually loads, so a missing asset never shows an empty frame.
   */
  PRODUCT_SCREENSHOT: '/images/join-product-screenshot.png',
  /**
   * CTA destination — the EMAIL-based agent signup path. OAuth is intentionally
   * bypassed here until the OAuth role bug (AGENT-02/03) is fixed.
   */
  SIGNUP_ROUTE: '/auth?mode=signup&role=agent',
  /** Sign-in destination for returning agents. */
  SIGNIN_ROUTE: '/auth?mode=login&role=agent',
} as const;
